import { SerializedStyles } from "@emotion/react";
import { Key, Ref, memo, useCallback, useState } from "react";
import { createStyles } from "src/styles";

import { colors } from "@fraction/shared";

import CheckboxGenericValue from "../CheckboxGenericValue";
import { RegularText } from "../v1/Text";

export interface CheckboxSelectorOption<V> {
  value: V;
  label: string;
}

export interface CheckboxSelectorProps<V> {
  options: CheckboxSelectorOption<V>[];
  value?: V[];
  label?: string;
  onChange?: (value: V[]) => void;
  focusRef?: Ref<HTMLDivElement>;
  style?: SerializedStyles;
  errorMessage?: string;
}

const styles = createStyles({
  container: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 30,
    "@media(max-width: 470px)": {
      gap: 15,
    },
  },
  label: {
    marginBottom: 16,
    display: "block",
  },
  errorText: {
    fontFamily: ["Montserrat", "sans-serif"].join(","),
    letterSpacing: "0.02em",
    marginTop: 12,
    color: colors.ERROR,
    fontSize: 12,
    maxWidth: "fit-content",
  },
  checkbox: {
    "@media(max-width: 470px)": {
      width: "100%",
    },
  },
});

function CheckboxSelector<V>({
  onChange,
  options,
  value,
  focusRef,
  label,
  style,
  errorMessage,
}: CheckboxSelectorProps<V>) {
  const [error, setError] = useState(false);

  const handleChange = useCallback(
    (checked: boolean, option?: V) => {
      setError(false);
      if (checked) {
        onChange?.([...(value || []), option as V]);
      } else if (value && value.length > 1) {
        onChange?.((value || []).filter((val) => val !== option));
      } else if (value && value.length === 1) {
        setError(true);
      }
    },
    [value, onChange]
  );

  return (
    <div css={style}>
      {label && (
        <RegularText paragraph style={styles.label}>
          {label}
        </RegularText>
      )}
      <div css={styles.container} ref={focusRef}>
        {options.map((option) => (
          <CheckboxGenericValue
            label={option.label}
            checked={value?.includes(option.value)}
            onChange={handleChange}
            key={option.label as Key}
            value={option.value}
            style={styles.checkbox}
          />
        ))}
      </div>
      {error && errorMessage && <div css={styles.errorText}>{errorMessage}</div>}
    </div>
  );
}

export default memo(CheckboxSelector) as typeof CheckboxSelector;
